import type { ActionId, ProfileId, TenantId } from './ids.js'

// ── Actions: the product primitive ────────────────────────────────────────────
// An action is data an admin authors in the dashboard; the runtime stays generic
// and only knows how to execute a small, fixed set of EFFECTS. Adding "make
// offer" or "schedule meeting" is a config row, not a code deploy.

export type ActionAudience = 'guest' | 'agent' | 'both'
export type ActionSurface  = 'toolbar' | 'inline' | 'quick_reply'

export interface ActionInputField {
  name:      string
  label:     string
  type:      'text' | 'number' | 'date' | 'select'
  required?: boolean
  options?:  string[]            // for type: 'select'
}

// A terminal effect produces a result and ends the action. Actions are
// single-shot: structured multi-step lives in the conversation state machine,
// and conversational multi-step is the `bot` effect — not an action workflow.
export type TerminalEffect =
  | { type: 'webhook';          url: string }                                  // signed POST to tenant system
  | { type: 'state_transition'; target: 'conversation' | 'subject'; toState: string }
  | { type: 'bot' }                                                            // route to the AI resolver
  | { type: 'builtin';          name: string }                                 // e.g. 'handoff'

// The ONLY composition allowed is "collect a form, then run one terminal
// effect" — exactly one level deep. This covers input-gathering (e.g. an offer
// amount) without becoming a workflow engine.
export type ActionEffect =
  | TerminalEffect
  | { type: 'form'; fields: ActionInputField[]; then: TerminalEffect }

export type ActionResult =
  | { kind: 'system_message'; template?: string }   // post a system line into the chat
  | { kind: 'card' }                                 // render the effect's response as a card
  | { kind: 'state_badge' }                          // reflect a state change
  | { kind: 'none' }

export interface ActionDef {
  id:                ActionId
  label:             string
  icon?:             string
  confirm?:          boolean
  audience:          ActionAudience
  surface:           ActionSurface
  availableInStates?: string[]    // conversation/subject states; omit = always available
  effect:            ActionEffect
  result:            ActionResult
}

// Client-safe projection of an action: enough for the widget to render it and
// collect inputs, but NONE of the effect internals (webhook URLs, transition
// targets) — those stay server-side and execute on `invoke`. The client filters
// by `availableInStates` locally against the current conversation state, so a
// state change needs no manifest round-trip; the server re-validates on invoke.
export interface ManifestAction {
  id:                ActionId
  label:             string
  icon?:             string
  confirm?:          boolean
  audience:          ActionAudience
  surface:           ActionSurface
  availableInStates?: string[]
  input?:            ActionInputField[]   // present when the action collects input (form effect)
}

/** Project an internal action to its client-safe manifest form. */
export function toManifestAction(a: ActionDef): ManifestAction {
  const input = a.effect.type === 'form' ? a.effect.fields : undefined
  return {
    id: a.id, label: a.label, audience: a.audience, surface: a.surface,
    ...(a.icon ? { icon: a.icon } : {}),
    ...(a.confirm ? { confirm: a.confirm } : {}),
    ...(a.availableInStates ? { availableInStates: a.availableInStates } : {}),
    ...(input ? { input } : {}),
  }
}

// ── Behavior profile (what "domain" becomes) ──────────────────────────────────
// A reusable, admin-composed bundle of actions + defaults + state machine. Not a
// built-in taxonomy — the 7 old templates become starter presets of this shape.
// `version` lets an in-flight invocation validate against a consistent snapshot.

/** Operating hours slot: 0=Sun … 6=Sat, times in "HH:MM" 24h local. */
export interface OperatingHoursSlot { day: 0|1|2|3|4|5|6; open: string; close: string }

export interface BehaviorProfile {
  id:           ProfileId
  tenantId:     TenantId
  name:         string
  actions:      ActionDef[]
  defaults:     {
    greeting?: string
    theme?:    { accent: string }
    e2e?:      boolean
    persona?:  string
    /** Paid-tier flag: when true, hides the "Powered by Relay" footer in the widget. */
    whiteLabel?: boolean
    /** White-label: serve/embed the widget from this hostname (e.g.
     *  "chat.acmeco.com"). Allowed automatically as a CORS origin for the
     *  control-plane API so the widget works from the custom domain. */
    customDomain?: string
  }
  states:       string[]
  initialState: string
  version:      number
  welcomeMessage?:  string               // first message guests see when opening the widget
  operatingHours?:  OperatingHoursSlot[] // empty/absent = always open
  offlineMessage?:  string               // shown outside operating hours instead of chat
  /** Base64-encoded ECDSA P-256 SPKI public key. When set, guest tokens must be
   *  signed JWTs — unsigned opaque tokens are rejected. */
  guestPublicKey?: string
  createdAt:    number
  updatedAt:    number
}
